import { PhoneProfileMock } from "@/components/mockups/Mockups";
import { PlanCard } from "@/components/pricing/PlanCard";
import { AudienceBlock, FeatureGrid, StatementBand } from "@/components/sections/ServiceBlocks";
import { FaqSection } from "@/components/sections/FaqSection";
import { FinalCta } from "@/components/sections/FinalCta";
import { TestimonialsSection } from "@/components/sections/TestimonialsSection";
import { TextLink } from "@/components/ui/Button";
import { FreeAuditButton, WhatsAppButton } from "@/components/ui/Cta";
import { PageHero } from "@/components/ui/PageHero";
import { SectionHeader } from "@/components/ui/SectionHeader";
import { SHOW_SISTEMA_LUCENTIA } from "@/config/features";
import { SEO_INSTAGRAM_MANAGEMENT_EN } from "@/config/seo.en";
import { SEO_INSTAGRAM_MANAGEMENT_ES } from "@/config/seo.es";
import { useContent } from "@/data/content";
import { getMockups } from "@/data/mockups";
import { usePricing } from "@/data/pricing";
import { usePageSeo } from "@/hooks/usePageSeo";
import { useLocale } from "@/i18n/useLocale";

export function InstagramManagementPage() {
  usePageSeo("/gestion-instagram");
  const locale = useLocale();
  const { instagramManagementPage: page, home } = useContent();
  const pricing = usePricing();
  const mock = getMockups(locale).profile;
  const crumb = (locale === "en" ? SEO_INSTAGRAM_MANAGEMENT_EN : SEO_INSTAGRAM_MANAGEMENT_ES).breadcrumb;

  return (
    <main id="main-content" tabIndex={-1} className="outline-none">
      <PageHero
        crumb={crumb}
        title={
          <>
            {page.hero.title} <span className="text-teal-strong">{page.hero.titleAccent}</span>
          </>
        }
        lead={<p>{page.hero.subtitle}</p>}
        actions={
          <>
            <FreeAuditButton location="ig_management_hero" variant="primary" />
            <WhatsAppButton location="ig_management_hero" message={page.cta.whatsappMessage} />
          </>
        }
        aside={<PhoneProfileMock {...mock} />}
      >
        <ul className="hero-rise mt-10 grid gap-x-6 gap-y-2 text-ink-soft sm:grid-cols-2" style={{ ["--delay" as string]: "240ms" }}>
          {page.hero.highlights.map((item) => (
            <li key={item} className="node">
              {item}
            </li>
          ))}
        </ul>
      </PageHero>

      <StatementBand
        title={
          <>
            {page.pitch.titleBefore} <span className="text-brand-secondary">{page.pitch.titleAccent}</span>{" "}
            {page.pitch.titleAfter}
          </>
        }
        body={page.pitch.subtitle}
      />

      <FeatureGrid
        id="includes-heading"
        title={page.includes.title}
        lead={page.hero.closing}
        items={page.includes.items}
        footer={<p className="font-display text-xl text-brand-primary">{page.includes.summary}</p>}
      />

      <AudienceBlock title={page.audience.title} items={page.audience.items} note={page.audience.note} />

      <section aria-labelledby="ig-plans-heading" className="bg-white">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 md:py-28 lg:px-8">
          <SectionHeader id="ig-plans-heading" title={pricing.page.plansTitle} lead={home.plans.subtitle} />
          <div className="mt-16 grid gap-6 sm:grid-cols-2 lg:grid-cols-4 lg:gap-5">
            {pricing.packs.map((pack, i) => (
              <PlanCard
                key={pack.id}
                pack={pack}
                pricing={pricing}
                postsLabel={home.plans.postsLabel}
                foundationLabel={home.plans.foundationLabel}
                delay={i * 80}
              />
            ))}
          </div>
          <p className="mt-8 text-sm text-ink-muted">
            {pricing.taxNote} {pricing.networkNote}
          </p>
          <TextLink to="/precios" className="mt-6">
            {home.plans.cta}
          </TextLink>
        </div>
      </section>

      {SHOW_SISTEMA_LUCENTIA ? (
        <section aria-labelledby="ig-method-heading" className="bg-brand-bg">
          <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 md:py-28 lg:px-8">
            <SectionHeader id="ig-method-heading" title={page.method.title} lead={page.method.subtitle} />
            <ol className="mt-12 flex flex-wrap gap-3">
              {page.method.steps.map((step, i) => (
                <li
                  key={step}
                  data-reveal=""
                  style={{ ["--reveal-delay" as string]: `${i * 70}ms` }}
                  className="flex items-center gap-3 rounded-full border border-brand-primary/12 bg-white py-2 pl-2 pr-5 font-semibold text-brand-primary"
                >
                  <span className="grid size-8 place-items-center rounded-full bg-brand-secondary text-sm text-brand-primary">{i + 1}</span>
                  {step}
                </li>
              ))}
            </ol>
            <TextLink to={page.method.href} className="mt-10">
              {page.method.ctaLabel}
            </TextLink>
          </div>
        </section>
      ) : null}

      <TestimonialsSection service="gestion-instagram" />
      <FaqSection path="/gestion-instagram" />
      <FinalCta
        location="ig_management_final"
        body={page.cta.description}
        whatsappLabel={page.cta.buttonLabel}
        whatsappMessage={page.cta.whatsappMessage}
      />
    </main>
  );
}
